import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import zeplinColor from '../../../theme/Color';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { ReactComponent as PrevIcon } from '../../../assets/arrow-prev.svg';
import { ReactComponent as NextIcon } from '../../../assets/arrow-next.svg';
import style from './CustomRangePicker.module.css';

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// Updated interface for single date selection
interface SingleDate {
  selectedDate: Date | null;
}

const CustomRangePicker = ({
  onDateChange,
  currentDateSelected,
  onPreviousMonth,
  onNextMonth,
  setCollapsed,
  internalDisplayDate,
  setInternalDisplayDate,
}: {
  onDateChange: (date: Dayjs | null) => void;
  currentDateSelected?: Dayjs;
  onPreviousMonth?: () => void; //  Optional navigation callback
  onNextMonth?: () => void;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  internalDisplayDate: dayjs.Dayjs;
  setInternalDisplayDate: React.Dispatch<React.SetStateAction<dayjs.Dayjs>>;
}) => {
  //  Remove separate currentDate state - use currentDateSelected instead
  const [dateSelection, setDateSelection] = useState<SingleDate>({
    selectedDate: internalDisplayDate.toDate() || dayjs().toDate(),
  });
  // const [internalDisplayDate, setInternalDisplayDate] = useState<Dayjs>(
  //   currentDateSelected || dayjs(),
  // );

  const [prevDisplayDate, setPrevDisplayDate] = useState<Dayjs | null>(null);

  //  Use currentDateSelected or default to today
  const displayDate = internalDisplayDate;
  console.log(internalDisplayDate, currentDateSelected, 'internalDisplayDate');
  //  Clear selection when month/year changes
  useEffect(() => {
    if (currentDateSelected) {
      console.log('CustomRangePicker: External update from parent Calendar:', {
        currentDateSelected: currentDateSelected.format('YYYY-MM-DD'),
        currentInternalDisplay: internalDisplayDate.format('YYYY-MM-DD'),
        shouldUpdate: !currentDateSelected.isSame(internalDisplayDate, 'month'),
      });

      //  Update internal display to match parent's current week date
      setInternalDisplayDate(currentDateSelected);
    }
  }, [currentDateSelected]);

  //  Clear selection when month/year changes
  useEffect(() => {
    if (prevDisplayDate) {
      const monthChanged = displayDate.month() !== prevDisplayDate.month();
      const yearChanged = displayDate.year() !== prevDisplayDate.year();

      if ((monthChanged || yearChanged) && !currentDateSelected) {
        console.log('CustomRangePicker: Month/Year changed, clearing selection:', {
          oldMonth: prevDisplayDate.month(),
          oldYear: prevDisplayDate.year(),
          newMonth: displayDate.month(),
          newYear: displayDate.year(),
        });

        // Clear the selection
        setDateSelection({ selectedDate: null });

        // Notify parent about cleared selection
        if (onDateChange) {
          onDateChange(null);
        }
      }
    }

    // Update previous display date
    setPrevDisplayDate(displayDate);
  }, [displayDate.month(), displayDate.year(), onDateChange]);
  const goToPreviousMonth = () => {
    // if (onPreviousMonth) {
    //   onPreviousMonth(); // Use parent's navigation handler
    // } else {
    // Fallback to internal navigation
    const newDate = displayDate.subtract(1, 'month');

    setInternalDisplayDate((prev) => prev.subtract(1, 'month'));
  };

  const goToNextMonth = () => {
    // if (onNextMonth) {
    //   onNextMonth(); // Use parent's navigation handler
    // } else {
    // Fallback to internal navigation
    const newDate = displayDate.add(1, 'month');

    setInternalDisplayDate((prev) => prev.add(1, 'month'));
  };
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const getWeekRange = (date: Dayjs) => {
    const startOfWeek = date.startOf('isoWeek'); // Monday
    const endOfWeek = date.endOf('isoWeek'); // Sunday

    return {
      start: startOfWeek,
      end: endOfWeek,
      startDate: startOfWeek.format('YYYY-MM-DD'),
      endDate: endOfWeek.format('YYYY-MM-DD'),
    };
  };

  //  Updated navigation to call parent component

  //  Updated to use displayDate instead of currentDate
  const getCalendarData = () => {
    const currentDateJS = displayDate.toDate();
    const firstDayOfMonth = new Date(currentDateJS.getFullYear(), currentDateJS.getMonth(), 1);
    const lastDayOfMonth = new Date(currentDateJS.getFullYear(), currentDateJS.getMonth() + 1, 0);

    // Adjust for Monday start (ISO week)
    let firstDayWeekday = firstDayOfMonth.getDay();
    firstDayWeekday = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1;

    const daysInMonth = lastDayOfMonth.getDate();
    const days = [];

    // Add previous month dates to complete the first week
    for (let i = 0; i < firstDayWeekday; i++) {
      const prevMonthDate = new Date(
        currentDateJS.getFullYear(),
        currentDateJS.getMonth(),
        -firstDayWeekday + i + 1,
      );
      days.push({
        day: prevMonthDate.getDate(),
        date: prevMonthDate,
        isCurrentMonth: false,
        isPreviousMonth: true,
        isSelectable: true,
      });
    }

    // Days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDateJS.getFullYear(), currentDateJS.getMonth(), day);
      days.push({
        day: day,
        date: date,
        isCurrentMonth: true,
        isPreviousMonth: false,
        isSelectable: true,
      });
    }

    // Add next month dates to complete the last week
    const totalCells = Math.ceil(days.length / 7) * 7;
    let nextMonthDay = 1; // Start from day 1 and increment
    for (let i = days.length; i < totalCells; i++) {
      const nextMonthDate = new Date(
        currentDateJS.getFullYear(),
        currentDateJS.getMonth() + 1,
        nextMonthDay, // Use incremented day instead of calculation
      );
      days.push({
        day: nextMonthDate.getDate(),
        date: nextMonthDate,
        isCurrentMonth: false,
        isPreviousMonth: false,
        isSelectable: true,
      });
      nextMonthDay++; // Increment for next iteration
    }

    return days;
  };

  const calendarDays = getCalendarData();

  //  Updated handle date click
  const handleDateClick = (dayData: any) => {
    if (!dayData || !dayData.isSelectable) return;

    const clickedDate = dayData.date;
    const clickedDayjs = dayjs(clickedDate);

    // Don't navigate months - just select the date and stay in current view
    // The week highlighting will show across months automatically

    const newDateSelection: SingleDate = {
      selectedDate: clickedDate,
    };

    setDateSelection(newDateSelection);

    if (onDateChange) {
      onDateChange(dayjs(clickedDate));
    }

    console.log('Date selected:', {
      date: clickedDate.toLocaleDateString(),
      weekRange: getWeekRange(dayjs(clickedDate)),
      stayingInMonth: displayDate.format('MMMM YYYY'),
    });
  };

  //  Updated helper functions to work with dayData objects
  const isToday = (dayData: any) => {
    if (!dayData.date) return false;
    const today = new Date();
    return (
      dayData.date.getDate() === today.getDate() &&
      dayData.date.getMonth() === today.getMonth() &&
      dayData.date.getFullYear() === today.getFullYear()
    );
  };

  const isSelectedDate = (dayData: any) => {
    return (
      dateSelection.selectedDate && dayData.date.getTime() === dateSelection.selectedDate.getTime()
    );
  };

  //  Check if date is week start (Monday)
  const isWeekStart = (dayData: any) => {
    if (!dateSelection.selectedDate || !dayData.date) return false;

    const selectedDateDayjs = dayjs(dateSelection.selectedDate);
    const currentDayjs = dayjs(dayData.date);
    const weekRange = getWeekRange(selectedDateDayjs);

    return currentDayjs.isSame(weekRange.start, 'day');
  };

  //  Check if date is week end (Sunday)
  const isWeekEnd = (dayData: any) => {
    if (!dateSelection.selectedDate || !dayData.date) return false;

    const selectedDateDayjs = dayjs(dateSelection.selectedDate);
    const currentDayjs = dayjs(dayData.date);
    const weekRange = getWeekRange(selectedDateDayjs);

    return currentDayjs.isSame(weekRange.end, 'day');
  };

  //  Check if date is in selected week range (but not start or end)
  const isInWeekRange = (dayData: any) => {
    if (!dateSelection.selectedDate || !dayData.date) return false;

    const selectedDateDayjs = dayjs(dateSelection.selectedDate);
    const currentDayjs = dayjs(dayData.date);
    const weekRange = getWeekRange(selectedDateDayjs);

    const isInWeek =
      currentDayjs.isSameOrAfter(weekRange.start, 'day') &&
      currentDayjs.isSameOrBefore(weekRange.end, 'day');

    const isStart = currentDayjs.isSame(weekRange.start, 'day');
    const isEnd = currentDayjs.isSame(weekRange.end, 'day');

    return isInWeek && !isStart && !isEnd;
  };

  //  Check if date is anywhere in selected week
  const isInSelectedWeek = (dayData: any) => {
    if (!dateSelection.selectedDate || !dayData.date) return false;

    const selectedDateDayjs = dayjs(dateSelection.selectedDate);
    const currentDayjs = dayjs(dayData.date);
    const weekRange = getWeekRange(selectedDateDayjs);

    return (
      currentDayjs.isSameOrAfter(weekRange.start, 'day') &&
      currentDayjs.isSameOrBefore(weekRange.end, 'day')
    );
  };

  //  Get container style for continuous background
  const getContainerStyle = (dayData: any) => {
    if (!dayData) return {};

    const isStart = isWeekStart(dayData);
    const isEnd = isWeekEnd(dayData);
    const inRange = isInWeekRange(dayData);

    if (isStart || isEnd || inRange) {
      return {
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '4px',
          bottom: '4px',
          left: isStart ? '50%' : '-2px',
          right: isEnd ? '50%' : '-2px',
          backgroundColor: zeplinColor.Neutral3,
          zIndex: 0,
        },
      };
    }
    return {};
  };

  //  Updated styling - make start/end transparent for continuous background
  const getDateStyle = (dayData: any) => {
    const isSelected = isSelectedDate(dayData);
    const today = isToday(dayData);
    const isStart = isWeekStart(dayData);
    const isEnd = isWeekEnd(dayData);
    const inRange = isInWeekRange(dayData);
    const inWeek = isInSelectedWeek(dayData);

    // Selected date (highest priority)
    if (isSelected) {
      return {
        backgroundColor: zeplinColor.BasicBlack,
        color: zeplinColor.Neutral1,
        fontWeight: 600,
        borderRadius: '50%',
        zIndex: 3,
      };
    }

    // Today's date (if not in selected week)
    else if (today && inWeek) {
      return {
        backgroundColor: zeplinColor.BasicBlack,
        color: zeplinColor.Neutral1,
        borderRadius: '50%',
        fontWeight: 600,
        zIndex: 2,
      };
    }

    //  Week start/end dates - transparent to show continuous background
    else if (isStart || isEnd) {
      return {
        backgroundColor: zeplinColor.Neutral3, // Let container background show
        color: dayData.isCurrentMonth ? zeplinColor.BasicBlack : zeplinColor.Neutral6,
        borderRadius: isWeekStart(dayData)
          ? '48px 0 0 48px'
          : isWeekEnd(dayData)
          ? '0 48px 48px 0'
          : '0',
        fontWeight: 600,
        zIndex: 2,
      };
    }

    // Middle dates in week range - transparent for continuous background
    else if (inRange) {
      return {
        backgroundColor: today ? zeplinColor.BasicBlack : 'transparent',
        color: today
          ? zeplinColor.Neutral1
          : dayData.isCurrentMonth
          ? zeplinColor.BasicBlack
          : zeplinColor.Neutral6,
        borderRadius: '50%',
        fontWeight: 500,
        zIndex: 1,
      };
    }

    // Current month dates
    else if (dayData.isCurrentMonth) {
      return {
        backgroundColor: 'transparent',
        color: zeplinColor.BasicBlack,
        borderRadius: '50%',
        fontWeight: 400,
        '&:hover': {
          backgroundColor: zeplinColor.Neutral3,
          transform: 'scale(1.05)',
        },
      };
    }

    // Previous/Next month dates
    else {
      return {
        backgroundColor: 'transparent',
        color: zeplinColor.Neutral6,
        borderRadius: '50%',
        fontWeight: 300,
      };
    }
  };

  // Clear date selection
  const clearSelection = () => {
    setDateSelection({ selectedDate: null });
    if (onDateChange) {
      onDateChange(null);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: zeplinColor.Neutral1,
        borderRadius: '12px',
        maxWidth: '400px',
        margin: '0 auto',
      }}
    >
      {/* Header with month navigation */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <Typography
          variant='responsiveHeading4'
          sx={{
            color: zeplinColor.BasicBlack,
            fontWeight: 600,
          }}
        >
          {/*  Use displayDate instead of separate state */}
          {monthNames[displayDate.month()]} {displayDate.year()}
        </Typography>
        <Box sx={{ display: 'flex', gap: '4px' }}>
          <Box
            sx={{
              padding: '5px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',

              borderRadius: '50%',
              '&:hover': { backgroundColor: zeplinColor.Neutral4 },
            }}
          >
            <PrevIcon
              className={style['next-prev-icon']}
              onClick={goToPreviousMonth}
              style={{ cursor: 'pointer' }}
            />
          </Box>

          <Box
            sx={{
              padding: '5px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',

              borderRadius: '50%',
              '&:hover': { backgroundColor: zeplinColor.Neutral4 },
            }}
          >
            <NextIcon
              className={style['next-prev-icon']}
              onClick={goToNextMonth}
              style={{ cursor: 'pointer' }}
            />
          </Box>
          <Box
            sx={{
              padding: '5px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              borderRadius: '50%',
              '&:hover': { backgroundColor: zeplinColor.Neutral4 },
            }}
            onClick={() => setCollapsed((prev) => !prev)}
          >
            <img src={'/assets/collapse-icon.svg'} />
          </Box>
        </Box>
      </Box>

      {/* Day headers */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '4px',
          marginBottom: '12px',
        }}
      >
        {dayNames.map((dayName) => (
          <Box
            key={dayName}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 4px',
            }}
          >
            <Typography
              variant='responsiveBody2'
              sx={{
                color: zeplinColor.BasicBlack,
                // fontWeight: 600,
              }}
            >
              {dayName}
            </Typography>
          </Box>
        ))}
      </Box>

      {/*  Updated calendar grid with no gap for continuous background */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '0px', // No gap for seamless connection
        }}
      >
        {calendarDays.map((dayData, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '44px',
              cursor: dayData && dayData.isCurrentMonth ? 'pointer' : 'default',
              position: 'relative',
              padding: '2px', // Small padding instead of gap
              // Only apply container style to current month dates
              ...getContainerStyle(dayData),
            }}
            onClick={() => dayData && dayData.isCurrentMonth && handleDateClick(dayData)}
          >
            {dayData && (
              <Box
                sx={{
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative',
                  transition: 'all 0.2s ease-in-out',
                  zIndex: 1,
                  ...getDateStyle(dayData),
                }}
              >
                <Typography
                  variant='responsiveBody3'
                  sx={{
                    // fontSize: '14px',

                    fontWeight:
                      isSelectedDate(dayData) || isToday(dayData)
                        ? 600
                        : isInSelectedWeek(dayData)
                        ? 500
                        : 400,
                  }}
                >
                  {dayData.day}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default CustomRangePicker;
